import { type OnboardingCardId, remainingOnboardingCards } from '../../lib/onboardingCardDismissal';

import { DEFAULT_HEIGHT_ESTIMATE_CONFIG, type HeightEstimateConfig, estimateItemHeight } from '../core/heights';
import { isRecord, stringField } from '../core/record';
import type { ChatAttachment, ChatItem } from '../core/types';
import {
  ATTACHMENT_TEXT_GAP_PX,
  attachmentInputsFromRendererRow,
  estimateAttachmentsHeight,
  hasMessageBodyAfterAttachments,
} from './attachmentGeometry';
import { estimateEmojiOnlyHeight } from './emojiGeometry';
import { END_OF_MESSAGES_FALLBACK_HEIGHT_PX, type EndOfMessagesHeightContext, endOfMessagesHeight } from './endOfMessagesGeometry';
import { ECHO_INVALID_MESSAGE_ROW_HEIGHT_PX, isInvalidEchoMessage } from './invalidMessage';
import { LINK_PREVIEW_LIST_GAP_PX, LINK_PREVIEW_LIST_PADDING_TOP_PX, estimateLinkPreviewHeight } from './linkPreviewGeometry';
import { resolveMessageTextContentWidth } from './messageBubbleGeometry';

const ECHO_HEIGHT_ESTIMATE_CONFIG = {
  ...DEFAULT_HEIGHT_ESTIMATE_CONFIG,
  estimatePreviewHeight: estimateLinkPreviewHeight,
  estimateAttachmentsHeight: estimateEchoAttachmentsHeight,
  estimateEmojiOnlyHeight,
  previewBlockPaddingTop: LINK_PREVIEW_LIST_PADDING_TOP_PX,
  previewGap: LINK_PREVIEW_LIST_GAP_PX,
} satisfies HeightEstimateConfig;

/** Narrowest content width the estimate models are calibrated for; below this the text
 * model diverges (height ~ 1/width), so degenerate transient pane widths are estimated
 * as if at the floor. Measurements are unaffected. */
export const MIN_ESTIMATE_CONTENT_WIDTH_PX = 320;

interface CurrentViewerProfileId {
  readonly current: string | null;
}

export interface CurrentEndOfMessagesHeightContext {
  readonly current: Omit<EndOfMessagesHeightContext, 'showOnboardingCards'> & {
    // showOnboardingCards is derived here (admin && cards left), not passed in.
    readonly isOnboardingAdmin: boolean;
    // Which cards the route's deck deals — DMs drop the invite card.
    readonly onboardingCardIds: readonly OnboardingCardId[];
    readonly profileId: string | null;
    readonly workspaceId: string | null;
  };
}

/** Builds a stable estimator whose ownership checks follow the current route profile. */
export function createEchoItemHeightEstimator(
  viewerProfileId: CurrentViewerProfileId,
  endOfMessagesContext?: CurrentEndOfMessagesHeightContext,
) {
  function resolveTextContentWidth(item: ChatItem, contentWidth: number): number {
    return resolveMessageTextContentWidth(Math.max(contentWidth, MIN_ESTIMATE_CONTENT_WIDTH_PX));
  }

  function estimateEmojiOnlyHeightAtCalibratedWidth(count: number, contentWidth: number): number {
    return estimateEmojiOnlyHeight(count, Math.max(contentWidth, MIN_ESTIMATE_CONTENT_WIDTH_PX));
  }

  const config = {
    ...ECHO_HEIGHT_ESTIMATE_CONFIG,
    estimateEmojiOnlyHeight: estimateEmojiOnlyHeightAtCalibratedWidth,
    resolveTextContentWidth,
  } satisfies HeightEstimateConfig;

  return function estimateEchoItemHeight(item: ChatItem, contentWidth: number): number {
    if (isInvalidEchoMessage(item)) return ECHO_INVALID_MESSAGE_ROW_HEIGHT_PX;
    if (isEndOfMessagesItem(item)) return estimateEndOfMessagesHeight(endOfMessagesContext);
    return estimateItemHeight(item, contentWidth, config);
  };
}

function estimateEndOfMessagesHeight(contextRef?: CurrentEndOfMessagesHeightContext): number {
  const context = contextRef?.current;
  if (!context) return END_OF_MESSAGES_FALLBACK_HEIGHT_PX;

  return endOfMessagesHeight({
    ...context,
    showOnboardingCards:
      context.isOnboardingAdmin && remainingOnboardingCards(context.workspaceId, context.profileId, context.onboardingCardIds).length > 0,
  });
}

export function isEndOfMessagesItem(item: ChatItem): boolean {
  return isRecord(item.rendererRow) && stringField(item.rendererRow, 'type') === 'end_of_messages';
}

function estimateEchoAttachmentsHeight(attachments: readonly ChatAttachment[], contentWidth: number, item?: ChatItem): number {
  const renderedAttachments = attachmentInputsFromRendererRow(item ?? { attachments });
  const height = estimateAttachmentsHeight(renderedAttachments, contentWidth);
  if (height === 0) return 0;
  return height + (hasMessageBodyAfterAttachments(item) ? ATTACHMENT_TEXT_GAP_PX : 0);
}
